import { supabase } from '../lib/supabase';

class TankMixingService {
  // Check if user has premium access to tank mixing features
  async hasPremiumAccess(userId) {
    try {
      const { data, error } = await supabase?.from('subscriptions')?.select('tier, status')?.eq('user_id', userId)?.eq('status', 'active')?.in('tier', ['commercial_premium', 'business'])?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking premium access:', error);
      return false;
    }
  }

  // Check compatibility between multiple products
  async checkProductCompatibility(productIds) {
    try {
      const { data, error } = await supabase?.rpc('check_tank_mixing_compatibility', { 
          product_ids: productIds 
        });

      if (error) throw error;
      return data?.[0] || { is_safe: true, warnings: [], dangerous_combinations: [] };
    } catch (error) {
      console.error('Error checking compatibility:', error);
      throw new Error('Failed to check product compatibility: ' + error.message);
    }
  }

  // Get W.A.L.E.S. mixing protocol steps
  async getWalesMixingOrder() {
    try {
      const { data, error } = await supabase?.rpc('get_wales_mixing_order');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting W.A.L.E.S. protocol:', error);
      throw new Error('Failed to load mixing protocol: ' + error.message);
    }
  }

  // Get all compatibility data for reference
  async getAllCompatibilityData() {
    try {
      const { data, error } = await supabase?.from('tank_mixing_compatibility')?.select(`
          *,
          product_a:products!tank_mixing_compatibility_product_a_id_fkey(id, name, active_ingredients),
          product_b:products!tank_mixing_compatibility_product_b_id_fkey(id, name, active_ingredients)
        `)?.order('compatibility_status', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting compatibility data:', error);
      throw new Error('Failed to load compatibility data: ' + error.message);
    }
  }

  // Save tank mixing procedure
  async saveTankMixingProcedure(userId, procedureData) {
    try {
      const { data, error } = await supabase?.from('tank_mixing_procedures')?.insert({
          user_id: userId,
          procedure_name: procedureData?.procedureName,
          target_pest_or_disease: procedureData?.targetPest,
          tank_size_gallons: procedureData?.tankSize,
          water_ph: procedureData?.waterPH,
          water_temperature: procedureData?.waterTemp,
          mixing_products: procedureData?.mixingProducts,
          wales_order_followed: procedureData?.walesOrderFollowed,
          compatibility_warnings: procedureData?.compatibilityWarnings,
          total_cost: procedureData?.totalCost,
          estimated_coverage_acres: procedureData?.estimatedCoverage,
          notes: procedureData?.notes,
          is_template: procedureData?.isTemplate || false
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving tank mixing procedure:', error);
      throw new Error('Failed to save tank mixing procedure: ' + error.message);
    }
  }

  // Get user's tank mixing procedures
  async getUserTankMixingProcedures(userId) {
    try {
      const { data, error } = await supabase?.from('tank_mixing_procedures')?.select(`
          *,
          user_tank_mixing_history(
            id, application_date, field_location, effectiveness_rating, notes
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting tank mixing procedures:', error);
      throw new Error('Failed to load tank mixing procedures: ' + error.message);
    }
  }

  // Get template procedures for quick start
  async getTemplateProcedures() {
    try {
      const { data, error } = await supabase?.from('tank_mixing_procedures')?.select('*')?.eq('is_template', true)?.order('procedure_name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting template procedures:', error);
      throw new Error('Failed to load template procedures: ' + error.message);
    }
  }

  // Record tank mixing application history
  async recordTankMixingApplication(userId, applicationData) {
    try {
      const { data, error } = await supabase?.from('user_tank_mixing_history')?.insert({
          user_id: userId,
          procedure_id: applicationData?.procedureId,
          application_date: applicationData?.applicationDate,
          field_location: applicationData?.fieldLocation,
          weather_conditions: applicationData?.weatherConditions,
          effectiveness_rating: applicationData?.effectivenessRating,
          notes: applicationData?.notes
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error recording tank mixing application:', error);
      throw new Error('Failed to record application: ' + error.message);
    }
  }

  // Delete tank mixing procedure
  async deleteTankMixingProcedure(procedureId, userId) {
    try {
      const { error } = await supabase?.from('tank_mixing_procedures')?.delete()?.eq('id', procedureId)?.eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting tank mixing procedure:', error);
      throw new Error('Failed to delete procedure: ' + error.message);
    }
  }

  // Update tank mixing procedure
  async updateTankMixingProcedure(procedureId, userId, updates) {
    try {
      const { data, error } = await supabase?.from('tank_mixing_procedures')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', procedureId)?.eq('user_id', userId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating tank mixing procedure:', error);
      throw new Error('Failed to update procedure: ' + error.message);
    }
  }
}

export const tankMixingService = new TankMixingService();
export default tankMixingService;